/**
 * The one way a page loads data. When a dep changes (a network switch), the
 * previous call is aborted, its late answer is dropped, and `data` is null from
 * the first render with the new deps: the screen never shows one network's
 * numbers under another's name. `reload()` keeps the current `data` until the
 * new answer lands; an aborted call is not an error.
 */

import { useCallback, useEffect, useRef, useState, type DependencyList } from "react";
import { isAbortError } from "./api";

export interface Loaded<T> {
  /** The last answer for the current deps, or null. */
  data: T | null;
  /** The last failure for the current deps (an ApiError, usually), or null. */
  error: unknown;
  /** True while a call for the current deps is in flight. */
  loading: boolean;
  /** Runs the call again, keeping `data` until the answer lands. */
  reload: () => void;
}

function sameDeps(a: DependencyList, b: DependencyList): boolean {
  return a.length === b.length && a.every((d, i) => Object.is(d, b[i]));
}

interface State<T> {
  /** The deps this state belongs to. */
  deps: DependencyList;
  data: T | null;
  error: unknown;
  loading: boolean;
}

export function useLoad<T>(load: (signal: AbortSignal) => Promise<T>, deps: DependencyList): Loaded<T> {
  const loadRef = useRef(load);
  loadRef.current = load;
  const [state, setState] = useState<State<T>>(() => ({ deps, data: null, error: null, loading: true }));
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    const current = deps;
    setState((prev) =>
      sameDeps(prev.deps, current)
        ? { ...prev, loading: true }
        : { deps: current, data: null, error: null, loading: true },
    );
    loadRef.current(controller.signal).then(
      (data) => {
        if (!controller.signal.aborted) setState({ deps: current, data, error: null, loading: false });
      },
      (error: unknown) => {
        if (controller.signal.aborted || isAbortError(error)) return;
        setState((prev) => ({
          deps: current,
          data: sameDeps(prev.deps, current) ? prev.data : null,
          error,
          loading: false,
        }));
      },
    );
    return () => controller.abort();
    // `deps` is the caller's list, spread in; `tick` is reload().
  }, [...deps, tick]);

  const reload = useCallback(() => setTick((n) => n + 1), []);
  if (!sameDeps(state.deps, deps)) return { data: null, error: null, loading: true, reload };
  return { data: state.data, error: state.error, loading: state.loading, reload };
}
